import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

const randInt = (min, max) => min + Math.floor(Math.random() * (max - min + 1));
const pick = (values) => values[Math.floor(Math.random() * values.length)];

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

const lcm = (a, b) => Math.abs(a * b) / gcd(a, b);
const coeffX = (value) => value === 1 ? "x" : value === -1 ? "-x" : `${value}x`;
const linearLatex = (a, b) => b === 0
  ? coeffX(a)
  : `${coeffX(a)} ${b > 0 ? "+" : "-"} ${Math.abs(b)}`;

export function generateTask() {
  while (true) {
    const [leftDenominator, rightDenominator] = pick([[2, 3], [3, 4], [4, 6], [3, 5]]);
    const commonMultiple = lcm(leftDenominator, rightDenominator);
    const leftMultiplier = commonMultiple / leftDenominator;
    const rightMultiplier = commonMultiple / rightDenominator;
    const solution = randInt(-6, 6);
    const leftCoefficient = pick([-5, -4, -3, -2, 2, 3, 4, 5]);
    const rightCoefficient = pick([-5, -4, -3, -2, 2, 3, 4, 5]);
    const leftConstant = pick([-6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6]);
    const scaledRightConstant =
      leftMultiplier * (leftCoefficient * solution + leftConstant)
      - rightMultiplier * rightCoefficient * solution;

    if (scaledRightConstant % rightMultiplier !== 0) continue;
    const rightConstant = scaledRightConstant / rightMultiplier;
    const reducedCoefficient =
      leftMultiplier * leftCoefficient - rightMultiplier * rightCoefficient;
    const reducedConstant =
      rightMultiplier * rightConstant - leftMultiplier * leftConstant;

    if (reducedCoefficient === 0 || rightConstant === 0 || Math.abs(rightConstant) > 14) continue;

    return {
      type: "LinearEquationFractionsTask",
      data: {
        leftDenominator,
        rightDenominator,
        commonMultiple,
        leftMultiplier,
        rightMultiplier,
        leftCoefficient,
        rightCoefficient,
        leftConstant,
        rightConstant,
        reducedCoefficient,
        reducedConstant,
        solution,
        latex: `\\dfrac{${linearLatex(leftCoefficient, leftConstant)}}{${leftDenominator}} = \\dfrac{${linearLatex(rightCoefficient, rightConstant)}}{${rightDenominator}}`,
      },
    };
  }
}

export function renderTask({ data }) {
  return <p>Rozwiąż równanie: <BlockMath math={data.latex} /></p>;
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-4">
      <InlineMath math="x=" />
      <FractionInput value={userAnswer || ""} onChange={onChange} disabled={disabled} label="x" />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const value = userAnswer?.parsed;
  return value != null && Number.isFinite(value) && Math.abs(value - data.solution) < 1e-6;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`x=${data.solution}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer?.raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(userAnswer.raw, "x")} />;
}

export function renderExplanation({ data }) {
  const {
    leftDenominator, rightDenominator, commonMultiple,
    leftMultiplier, rightMultiplier, leftCoefficient, rightCoefficient,
    leftConstant, rightConstant, reducedCoefficient, reducedConstant,
    solution,
  } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Mianowniki to <InlineMath math={`${leftDenominator}`} /> i{" "}
        <InlineMath math={`${rightDenominator}`} />, więc ich najmniejsza wspólna wielokrotność to{" "}
        <InlineMath math={`\\operatorname{NWW}(${leftDenominator},${rightDenominator})=${commonMultiple}`} />.
        Mnożymy przez nią <strong>obie strony równania</strong>.
      </p>
      <BlockMath math={`${commonMultiple}\\cdot\\dfrac{${linearLatex(leftCoefficient, leftConstant)}}{${leftDenominator}}=${commonMultiple}\\cdot\\dfrac{${linearLatex(rightCoefficient, rightConstant)}}{${rightDenominator}}`} />
      <BlockMath math={`${leftMultiplier}(${linearLatex(leftCoefficient, leftConstant)})=${rightMultiplier}(${linearLatex(rightCoefficient, rightConstant)})`} />
      <p><strong>Krok 2.</strong> Rozwijamy nawiasy i przenosimy wyrazy.</p>
      <BlockMath math={`${coeffX(leftMultiplier * leftCoefficient)} ${leftMultiplier * leftConstant >= 0 ? "+" : "-"} ${Math.abs(leftMultiplier * leftConstant)} = ${coeffX(rightMultiplier * rightCoefficient)} ${rightMultiplier * rightConstant >= 0 ? "+" : "-"} ${Math.abs(rightMultiplier * rightConstant)}`} />
      <BlockMath math={`${coeffX(reducedCoefficient)}=${reducedConstant}`} />
      <BlockMath math={`x=${solution}`} />
    </div>
  );
}
